import React from 'react';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PlaceAddressReadOnlyFields from '../address/PlaceAddressReadOnlyFields';
import type { Space } from '../../models/Space';
import {
  placeAddressDraftFromRecord,
  placeAddressRecordFromSpace,
} from '../../utils/placeAddress';

export interface TutorBusinessLessonLocationSelectProps {
  spaces: Space[];
  loading: boolean;
  loadError: string | null;
  selectedSpaceId: string | null;
  onSpaceChange: (spaceId: string) => void;
  selectionError?: string | null;
  compact?: boolean;
  readOnly?: boolean;
}

function spaceHasAddress(space: Space): boolean {
  return Boolean(
    space.addressLine1?.trim() ||
      space.city?.trim() ||
      space.formattedAddress?.trim() ||
      space.placeName?.trim() ||
      (space.latitude != null && space.longitude != null)
  );
}

const TutorBusinessLessonLocationSelect: React.FC<TutorBusinessLessonLocationSelectProps> = ({
  spaces,
  loading,
  loadError,
  selectedSpaceId,
  onSpaceChange,
  selectionError,
  compact = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const selectedSpace = spaces.find((space) => space.id === selectedSpaceId) ?? null;

  return (
    <Box>
      {!compact ? (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2">
            {t('tutor.profileCompletion.lessonLocationTitle', 'Lesson location')}
          </Typography>
        </Stack>
      ) : null}

      {loadError ? (
        <Alert severity="error" sx={{ mb: compact ? 1 : 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {!loadError && !loading && spaces.length === 0 ? (
        <Alert severity="warning" sx={{ mb: compact ? 1 : 2 }}>
          {t(
            'tutor.profileCompletion.lessonLocationNoSpaces',
            'Create a space in organization settings before offering in-person lessons.',
          )}
        </Alert>
      ) : null}

      <FormControl fullWidth size="small" error={Boolean(selectionError)} disabled={readOnly || loading || spaces.length === 0}>
        <InputLabel id="tutor-lesson-space-label">
          {t('tutor.profileCompletion.lessonLocationSpaceLabel', 'Lesson space')}
        </InputLabel>
        <Select
          labelId="tutor-lesson-space-label"
          value={selectedSpaceId ?? ''}
          label={t('tutor.profileCompletion.lessonLocationSpaceLabel', 'Lesson space')}
          onChange={(event) => onSpaceChange(event.target.value)}
          readOnly={readOnly}
        >
          {spaces.map((space) => (
            <MenuItem key={space.id} value={space.id}>
              {space.name}
            </MenuItem>
          ))}
        </Select>
        {selectionError ? <FormHelperText>{selectionError}</FormHelperText> : null}
      </FormControl>

      {selectedSpace ? (
        <Box sx={{ mt: compact ? 1 : 2 }}>
          {spaceHasAddress(selectedSpace) ? (
            <PlaceAddressReadOnlyFields
              value={placeAddressDraftFromRecord(placeAddressRecordFromSpace(selectedSpace))}
              placeLocationFallback={selectedSpace.formattedAddress || selectedSpace.placeName}
              compact={compact}
            />
          ) : (
            <Typography variant="body2" color="warning.main">
              {t(
                'tutor.profileCompletion.lessonLocationSpaceAddressMissing',
                'This space has no address yet. Add one in space settings.',
              )}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default TutorBusinessLessonLocationSelect;
